import { USER_TYPE } from "../system.js";
import { CORE_PERMISSIONS } from "./corePermissionKeys.js";
import { PERMISSION_GROUPS } from "./permissionGroups.js";
export const PERMISSION_METADATA = {
    // Dashboard permissions
    [CORE_PERMISSIONS.VIEW_BOOKING_DASHBOARD]: {
        group: PERMISSION_GROUPS.DASHBOARDS,
        entityKeys: ["bookingEntityName", "userEntityName"],
        nameTemplate: "View {bookingEntityName} Dashboard",
        descriptionTemplate: "Allows {userEntityName} to view and monitor the {bookingEntityName} metrics",
    },
    [CORE_PERMISSIONS.VIEW_SURVEY_DASHBOARD]: {
        group: PERMISSION_GROUPS.DASHBOARDS,
        entityKeys: ["surveyEntityName", "userEntityName"],
        nameTemplate: "View {surveyEntityName} Dashboard",
        descriptionTemplate: "Allows {userEntityName} to view and monitor the {surveyEntityName} metrics",
    },
    // User management permissions
    [CORE_PERMISSIONS.MANAGE_ADMINISTRATORS]: {
        group: PERMISSION_GROUPS.USERS,
        entityKeys: ["userEntityName"],
        nameTemplate: "Manage Administrator",
        descriptionTemplate: "Allows {userEntityName} to manage Administrator users",
    },
    [CORE_PERMISSIONS.MANAGE_MANAGERS]: {
        group: PERMISSION_GROUPS.USERS,
        entityKeys: ["managersEntityName", "managerEntityName", "userEntityName"],
        nameTemplate: "Manage {managersEntityName}",
        descriptionTemplate: "Allows {userEntityName} to manage the {managerEntityName} users",
    },
    [CORE_PERMISSIONS.MANAGE_ACCESS_ROLES]: {
        group: PERMISSION_GROUPS.USERS,
        entityKeys: ["managerEntityName", "adminsEntityName"],
        nameTemplate: "Manage Access Roles",
        descriptionTemplate: "Allows {adminsEntityName} to manage the {managerEntityName}/{adminsEntityName} Access Roles",
    },
    [CORE_PERMISSIONS.MANAGE_RECIPIENTS]: {
        group: PERMISSION_GROUPS.USERS,
        entityKeys: ["adminsEntityName"],
        nameTemplate: "Manage Recipients",
        descriptionTemplate: "Allows {adminsEntityName} to manage the Recipients",
    },
    // Respondent permissions
    [CORE_PERMISSIONS.MANAGE_RESPONDENTS]: {
        group: PERMISSION_GROUPS.USERS,
        entityKeys: [
            "respondentsEntityName",
            "respondentEntityName",
            "adminsEntityName",
        ],
        nameTemplate: "Manage {respondentsEntityName}",
        descriptionTemplate: "Allows {adminsEntityName} to manage the {respondentEntityName} users",
    },
    [CORE_PERMISSIONS.RESPONDENT_LIST]: {
        group: PERMISSION_GROUPS.RESPONDENTS,
        entityKeys: [
            "respondentsEntityName",
            "respondentEntityName",
            "userEntityName",
        ],
        nameTemplate: "View List of {respondentsEntityName}",
        descriptionTemplate: "Allows {userEntityName} to view the list of assigned {respondentEntityName}s",
    },
    [CORE_PERMISSIONS.RESPONDENT_READ]: {
        group: PERMISSION_GROUPS.RESPONDENTS,
        entityKeys: ["respondentEntityName", "userEntityName"],
        nameTemplate: "View Profile",
        descriptionTemplate: "Allows {userEntityName} to view the {respondentEntityName}'s profile including profile details",
    },
    [CORE_PERMISSIONS.RESPONDENT_WRITE]: {
        group: PERMISSION_GROUPS.RESPONDENTS,
        entityKeys: [
            "respondentEntityName",
            "respondentsEntityName",
            "userEntityName",
            "surveyTypeEntityName",
        ],
        nameTemplate: "Create and Edit {respondentEntityName}",
        descriptionTemplate: "Allows {userEntityName} to create and edit {respondentsEntityName} details including email and phone. Allows setting of companies and {surveyTypeEntityName} to the {respondentEntityName}",
    },
    [CORE_PERMISSIONS.RESPONDENT_DOCUMENT_READ]: {
        group: PERMISSION_GROUPS.RESPONDENTS,
        entityKeys: ["respondentEntityName", "userEntityName"],
        nameTemplate: "View Document",
        descriptionTemplate: "Allows {userEntityName} to view documents that were uploaded for this {respondentEntityName}",
    },
    [CORE_PERMISSIONS.RESPONDENT_DOCUMENT_WRITE]: {
        group: PERMISSION_GROUPS.RESPONDENTS,
        entityKeys: ["respondentEntityName", "userEntityName"],
        nameTemplate: "Upload Document",
        descriptionTemplate: "Allows {userEntityName} to upload documents for this {respondentEntityName}'s profile",
    },
    [CORE_PERMISSIONS.RESPONDENT_NOTE_READ]: {
        group: PERMISSION_GROUPS.RESPONDENTS,
        entityKeys: ["respondentEntityName", "userEntityName"],
        nameTemplate: "View Notes",
        descriptionTemplate: "Allows {userEntityName} to view notes created for this {respondentEntityName}",
    },
    [CORE_PERMISSIONS.RESPONDENT_NOTE_WRITE]: {
        group: PERMISSION_GROUPS.RESPONDENTS,
        entityKeys: ["respondentEntityName", "userEntityName"],
        nameTemplate: "Create and Delete Notes",
        descriptionTemplate: "Allows {userEntityName} to create and delete notes for this {respondentEntityName}",
    },
    [CORE_PERMISSIONS.RESPONDENT_CONTACT_READ]: {
        group: PERMISSION_GROUPS.RESPONDENTS,
        entityKeys: ["respondentEntityName", "userEntityName"],
        nameTemplate: "View Contacts",
        descriptionTemplate: "Allows {userEntityName} to view contacts created for this {respondentEntityName}",
    },
    [CORE_PERMISSIONS.RESPONDENT_CONTACT_WRITE]: {
        group: PERMISSION_GROUPS.RESPONDENTS,
        entityKeys: ["respondentEntityName", "userEntityName"],
        nameTemplate: "Create and Edit Contacts",
        descriptionTemplate: "Allows {userEntityName} to create and edit contacts for this {respondentEntityName}",
    },
    [CORE_PERMISSIONS.MANAGE_RESPONDENT_COMPANIES]: {
        group: PERMISSION_GROUPS.USERS,
        entityKeys: ["respondentEntityName", "adminsEntityName"],
        nameTemplate: "Manage {respondentEntityName}'s Company",
        descriptionTemplate: "Allows {adminsEntityName} to assign a company to the {respondentEntityName}",
        subPermission: true,
    },
    [CORE_PERMISSIONS.MANAGE_RESPONDENT_CONTACTS]: {
        group: PERMISSION_GROUPS.USERS,
        entityKeys: ["respondentEntityName", "adminsEntityName"],
        nameTemplate: "Manage {respondentEntityName}'s Contacts",
        descriptionTemplate: "Allows {adminsEntityName} to assign a contact to the {respondentEntityName}",
        subPermission: true,
    },
    // Booking permissions
    [CORE_PERMISSIONS.MANAGE_BOOKINGS]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ["bookingsEntityName", "adminsEntityName"],
        nameTemplate: "Manage {bookingsEntityName}",
        descriptionTemplate: "Allows {adminsEntityName} to manage the {bookingsEntityName}",
    },
    [CORE_PERMISSIONS.BOOKING_READ]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: [
            "bookingsEntityName",
            "extUserEntityName",
            "respondentEntityName",
            "managerEntityName",
            "managersEntityName",
        ],
        nameTemplate: "View {bookingsEntityName}",
        descriptionByUserType: {
            [USER_TYPE.MANAGER]: "<p>Allows {managerEntityName} to view:</p><ul><li>{bookingsEntityName} made to any {managerEntityName} at your location and</li><li>{bookingsEntityName} specifically assigned to you as {managerEntityName} at your location</li></ul><p>It does not allows to view {bookingsEntityName} specifically assigned to other {managersEntityName} at your location.</p>",
            [USER_TYPE.EXTERNAL_USER]: "<p>Allows {extUserEntityName} to view {bookingsEntityName.toLowerCase()} as long as:</p><ul><li>{extUserEntityName} is assigned to the {respondentEntityName} or</li><li>{respondentEntityName} currently belongs to a company that is associated to the {extUserEntityName}</li></ul>",
        },
    },
    [CORE_PERMISSIONS.BOOKING_READ_INCLUDE_ALL_RESPONDENT_BOOKINGS]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: [
            "bookingsEntityName",
            "bookingEntityName",
            "respondentEntityName",
            "extUserEntityName",
            "managerEntityName",
        ],
        nameTemplate: "View All {bookingsEntityName}",
        descriptionByUserType: {
            [USER_TYPE.MANAGER]: "<p>Allows {managerEntityName} to view:</p><ul><li>Every {bookingEntityName} made at your location</li></ul>",
            [USER_TYPE.EXTERNAL_USER]: "<p>Allows {managerEntityName} to view:</p><ul><li>Every {bookingEntityName} belonging to {respondentEntityName} assigned to the {extUserEntityName}</li></ul>",
        },
        subPermission: true,
    },
    [CORE_PERMISSIONS.BOOKING_READ_INCLUDE_AFFILIATE_BOOKINGS]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: [
            "bookingsEntityName",
            "bookingEntityName",
            "managerEntityName",
        ],
        nameTemplate: "View Affiliate {bookingsEntityName}",
        descriptionTemplate: "Allows {managerEntityName} to view affiliate {bookingEntityName}s",
        subPermission: true,
    },
    [CORE_PERMISSIONS.BOOKING_WRITE]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ["bookingEntityName", "userEntityName"],
        nameTemplate: "Edit {bookingEntityName} Details",
        descriptionTemplate: "Allows {userEntityName} to change the status and details (i.e. date and time, service and location) of a {bookingEntityName}.",
    },
    [CORE_PERMISSIONS.BOOKING_CREATE_VIA_BOOKING_PAGE]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: [
            "bookingsEntityName",
            "bookingEntityName",
            "bookingPageEntityName",
            "bookingPagesEntityName",
        ],
        nameTemplate: "Create {bookingsEntityName} via {bookingPageEntityName}",
        descriptionTemplate: "Allows making a {bookingEntityName} from {bookingPagesEntityName}.",
    },
    [CORE_PERMISSIONS.BOOKING_CREATE_VIA_CALENDAR]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ["bookingsEntityName", "userEntityName", "managerEntityName"],
        nameTemplate: "Create {bookingsEntityName} via Calendar",
        descriptionTemplate: "Allows making a {bookingsEntityName} through Calendar. {userEntityName} that has this permission will be able to book at any location with any {managerEntityName.toLowerCase()}.",
    },
    [CORE_PERMISSIONS.EVENT_CREATE_VIA_CALENDAR]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        nameTemplate: "Create/Update Event via Calendar",
        descriptionTemplate: "Allows making and updating an Event through Calendar.",
    },
    [CORE_PERMISSIONS.BOOKING_DOCUMENT_READ]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ["bookingEntityName", "userEntityName"],
        nameTemplate: "View {bookingEntityName} Document",
        descriptionTemplate: "Allows {userEntityName} to see {bookingEntityName} documents added to a {bookingEntityName}",
    },
    [CORE_PERMISSIONS.BOOKING_DOCUMENT_WRITE]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ["bookingEntityName", "userEntityName"],
        nameTemplate: "Upload {bookingEntityName} Document",
        descriptionTemplate: "Allows {userEntityName} to upload a {bookingEntityName} document for a {bookingEntityName}",
    },
    [CORE_PERMISSIONS.BOOKING_CALENDAR_READ]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ["bookingsEntityName", "bookingEntityName", "userEntityName"],
        nameTemplate: "View {bookingsEntityName} via {bookingEntityName} Calendar",
        descriptionTemplate: "Allows {userEntityName} to view {bookingsEntityName} via the Calendar",
    },
    [CORE_PERMISSIONS.MANAGE_BOOKING_COMPANIES]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ["bookingEntityName", "adminsEntityName"],
        nameTemplate: "Manage {bookingEntityName}'s Company",
        descriptionTemplate: "Allows {adminsEntityName} to assign a company to the {bookingEntityName}",
        subPermission: true,
    },
    [CORE_PERMISSIONS.MANAGE_CONFIRMATION_TEXT]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ["bookingEntityName", "adminsEntityName"],
        nameTemplate: "Manage Confirmation Text",
        descriptionTemplate: "Allows {adminsEntityName} to manage the {bookingEntityName} Confirmation Text",
    },
    [CORE_PERMISSIONS.MANAGE_SERVICES_LOCATIONS_BOOKING_PAGES]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ["bookingPagesEntityName", "adminsEntityName"],
        nameTemplate: "Manage Services, Locations and {bookingPagesEntityName}",
        descriptionTemplate: "Allows {adminsEntityName} to manage the Services, Locations and {bookingPagesEntityName}",
    },
    [CORE_PERMISSIONS.MANAGE_BOOKING_PAGE_COMPANIES]: {
        group: PERMISSION_GROUPS.BOOKINGS,
        entityKeys: ["bookingPageEntityName", "adminsEntityName"],
        nameTemplate: "Manage Booking Page's Company",
        descriptionTemplate: "Allows {adminsEntityName} to assign a company to the {bookingPageEntityName}",
        subPermission: true,
    },
    // Survey permissions
    [CORE_PERMISSIONS.MANAGE_SURVEY_TYPES]: {
        group: PERMISSION_GROUPS.SURVEYS,
        entityKeys: ["surveyTypesEntityName", "adminsEntityName"],
        nameTemplate: "Manage {surveyTypesEntityName}",
        descriptionTemplate: "Allows {adminsEntityName} to manage the {surveyTypesEntityName}",
    },
    [CORE_PERMISSIONS.MANAGE_SURVEY_BACKGROUNDS]: {
        group: PERMISSION_GROUPS.SURVEYS,
        entityKeys: ["surveyEntityName", "adminsEntityName"],
        nameTemplate: "Manage {surveyEntityName} Backgrounds",
        descriptionTemplate: "Allows {adminsEntityName} to manage the {surveyEntityName} Backgrounds",
    },
    [CORE_PERMISSIONS.SURVEY_READ]: {
        group: PERMISSION_GROUPS.SURVEYS,
        entityKeys: [
            "surveyEntityName",
            "surveysEntityName",
            "respondentEntityName",
            "userEntityName",
        ],
        nameTemplate: "View {surveyEntityName}",
        descriptionTemplate: "Allows {userEntityName} to view {surveysEntityName} created for this {respondentEntityName}",
    },
    [CORE_PERMISSIONS.SURVEY_WRITE]: {
        group: PERMISSION_GROUPS.SURVEYS,
        entityKeys: [
            "surveyEntityName",
            "surveysEntityName",
            "respondentEntityName",
            "userEntityName",
        ],
        nameTemplate: "Create and Answer {surveyEntityName}",
        descriptionTemplate: "Allows {userEntityName} to create {surveysEntityName} and answer them for this {respondentEntityName}",
    },
    [CORE_PERMISSIONS.VIEW_REPORTS]: {
        group: PERMISSION_GROUPS.SURVEYS,
        entityKeys: ["surveyEntityName", "adminsEntityName"],
        nameTemplate: "View Reports",
        descriptionTemplate: "Allows {adminsEntityName} to view the {surveyEntityName} Reports",
    },
    [CORE_PERMISSIONS.VIEW_FLAGGED_QUESTIONS]: {
        group: PERMISSION_GROUPS.SURVEYS,
        entityKeys: ["surveysEntityName", "userEntityName"],
        nameTemplate: "View Flagged Questions",
        descriptionTemplate: "Allows {userEntityName} to view flagged questions in {surveysEntityName}, and create a link for reviewing flagged question.",
    },
    [CORE_PERMISSIONS.SURVEY_COMPLETED_SEND]: {
        group: PERMISSION_GROUPS.SURVEYS,
        entityKeys: ["surveyEntityName", "surveysEntityName"],
        nameTemplate: "Send Completed {surveyEntityName}",
        descriptionTemplate: "Allow sending completed {surveysEntityName}",
    },
    [CORE_PERMISSIONS.VIEW_BOOKING_PAGE_SURVEYS]: {
        group: PERMISSION_GROUPS.SURVEYS,
        entityKeys: [
            "extUserEntityName",
            "surveysEntityName",
            "bookingPageEntityName",
        ],
        nameTemplate: "View {bookingPageEntityName} {surveysEntityName}",
        descriptionTemplate: "Allows {extUserEntityName} to view {surveysEntityName} originating from the {bookingPageEntityName}",
    },
    // Invoicing and Payments permissions
    [CORE_PERMISSIONS.CONFIGURE_INVOICING_AND_PAYMENTS]: {
        group: PERMISSION_GROUPS.INVOICING,
        entityKeys: ["adminsEntityName"],
        nameTemplate: "Configure Invoicing and Payments",
        descriptionTemplate: "Allows {adminsEntityName.toLowerCase()} to manage all invoicing and payment settings including system configurations, tax rates, and items.",
    },
    [CORE_PERMISSIONS.MANAGE_INVOICING_AND_PAYMENTS_SYSTEM_CONFIG]: {
        group: PERMISSION_GROUPS.INVOICING,
        entityKeys: ["adminsEntityName"],
        nameTemplate: "Manage System Configuration",
        descriptionTemplate: "Allows {adminsEntityName.toLowerCase()} to configure system invoicing and payment settings.",
        subPermission: true,
    },
    [CORE_PERMISSIONS.MANAGE_TAX_RATES]: {
        group: PERMISSION_GROUPS.INVOICING,
        entityKeys: ["adminsEntityName"],
        nameTemplate: "Manage Tax Rates",
        descriptionTemplate: "Allows {adminsEntityName.toLowerCase()} to create, edit and delete tax rates used in invoices and payments.",
        subPermission: true,
    },
    [CORE_PERMISSIONS.MANAGE_ITEMS]: {
        group: PERMISSION_GROUPS.INVOICING,
        entityKeys: ["adminsEntityName"],
        nameTemplate: "Manage Items",
        descriptionTemplate: "Allows {adminsEntityName.toLowerCase()} to create, edit and delete items used in invoices and payments.",
        subPermission: true,
    },
    [CORE_PERMISSIONS.MANAGE_ACTIVITY_ITEMS]: {
        group: PERMISSION_GROUPS.INVOICING,
        entityKeys: ["adminsEntityName"],
        nameTemplate: "Manage Activity Items",
        descriptionTemplate: "Allows {adminsEntityName.toLowerCase()} to configure billing items for different activity types (booking, survey, notes, and documents).",
        subPermission: true,
    },
    // Invoicing permissions for managers and external users
    [CORE_PERMISSIONS.MANAGE_INVOICES]: {
        group: PERMISSION_GROUPS.INVOICING,
        entityKeys: ["managerEntityName", "extUserEntityName", "adminsEntityName"],
        nameTemplate: "Manage Invoices",
        descriptionByUserType: {
            [USER_TYPE.ADMIN]: "Allows {adminsEntityName.toLowerCase()} to manage all invoices.",
            [USER_TYPE.MANAGER]: "Allows {managerEntityName.toLowerCase()} to manage invoices that they have access to.",
            [USER_TYPE.EXTERNAL_USER]: "Allows {extUserEntityName.toLowerCase()} to manage invoices that they have access to.",
        },
    },
    [CORE_PERMISSIONS.VIEW_INVOICES]: {
        group: PERMISSION_GROUPS.INVOICING,
        entityKeys: ["managerEntityName", "extUserEntityName", "adminsEntityName"],
        nameTemplate: "View Invoices",
        descriptionByUserType: {
            [USER_TYPE.ADMIN]: "Allows {adminsEntityName.toLowerCase()} to view all invoices.",
            [USER_TYPE.MANAGER]: "Allows {managerEntityName.toLowerCase()} to view invoices that they have access to.",
            [USER_TYPE.EXTERNAL_USER]: "Allows {extUserEntityName.toLowerCase()} to view invoices that they have access to.",
        },
        subPermission: true,
    },
    [CORE_PERMISSIONS.CREATE_INVOICE]: {
        group: PERMISSION_GROUPS.INVOICING,
        entityKeys: ["managerEntityName", "extUserEntityName", "adminsEntityName"],
        nameTemplate: "Create Invoice",
        descriptionByUserType: {
            [USER_TYPE.ADMIN]: "Allows {adminsEntityName.toLowerCase()} to create new invoices.",
            [USER_TYPE.MANAGER]: "Allows {managerEntityName.toLowerCase()} to create new invoices for records they have access to.",
            [USER_TYPE.EXTERNAL_USER]: "Allows {extUserEntityName.toLowerCase()} to create new invoices for records they have access to.",
        },
        subPermission: true,
    },
    [CORE_PERMISSIONS.EDIT_INVOICE]: {
        group: PERMISSION_GROUPS.INVOICING,
        entityKeys: ["managerEntityName", "extUserEntityName", "adminsEntityName"],
        nameTemplate: "Edit Invoice",
        descriptionByUserType: {
            [USER_TYPE.ADMIN]: "Allows {adminsEntityName.toLowerCase()} to edit existing invoices.",
            [USER_TYPE.MANAGER]: "Allows {managerEntityName.toLowerCase()} to edit existing invoices for records they have access to.",
            [USER_TYPE.EXTERNAL_USER]: "Allows {extUserEntityName.toLowerCase()} to edit existing invoices for records they have access to.",
        },
        subPermission: true,
    },
    [CORE_PERMISSIONS.DELETE_INVOICE]: {
        group: PERMISSION_GROUPS.INVOICING,
        entityKeys: ["managerEntityName", "extUserEntityName", "adminsEntityName"],
        nameTemplate: "Delete Invoice",
        descriptionByUserType: {
            [USER_TYPE.ADMIN]: "Allows {adminsEntityName.toLowerCase()} to delete invoices.",
            [USER_TYPE.MANAGER]: "Allows {managerEntityName.toLowerCase()} to delete invoices for records they have access to.",
            [USER_TYPE.EXTERNAL_USER]: "Allows {extUserEntityName.toLowerCase()} to delete invoices for records they have access to.",
        },
        subPermission: true,
    },
    [CORE_PERMISSIONS.CHANGE_INVOICE_STATUS]: {
        group: PERMISSION_GROUPS.INVOICING,
        entityKeys: ["managerEntityName", "extUserEntityName", "adminsEntityName"],
        nameTemplate: "Change Invoice Status",
        descriptionByUserType: {
            [USER_TYPE.ADMIN]: "Allows {adminsEntityName.toLowerCase()} to change the status of invoices.",
            [USER_TYPE.MANAGER]: "Allows {managerEntityName.toLowerCase()} to change the status of invoices for records they have access to.",
            [USER_TYPE.EXTERNAL_USER]: "Allows {extUserEntityName.toLowerCase()} to change the status of invoices for records they have access to.",
        },
        subPermission: true,
    },
    [CORE_PERMISSIONS.EXPORT_AND_SEND_INVOICE]: {
        group: PERMISSION_GROUPS.INVOICING,
        entityKeys: ["managerEntityName", "extUserEntityName", "adminsEntityName"],
        nameTemplate: "Export and Send Invoices",
        descriptionByUserType: {
            [USER_TYPE.ADMIN]: "Allows {adminsEntityName.toLowerCase()} to export and send invoices.",
            [USER_TYPE.MANAGER]: "Allows {managerEntityName.toLowerCase()} to export and send invoices for records they have access to.",
            [USER_TYPE.EXTERNAL_USER]: "Allows {extUserEntityName.toLowerCase()} to export and send invoices for records they have access to.",
        },
        subPermission: true,
    },
    // Charges and Payments permissions
    [CORE_PERMISSIONS.MANAGE_CHARGES_AND_PAYMENTS]: {
        group: PERMISSION_GROUPS.INVOICING,
        entityKeys: ["managerEntityName", "extUserEntityName", "adminsEntityName"],
        nameTemplate: "Manage Charges and Payments",
        descriptionByUserType: {
            [USER_TYPE.ADMIN]: "Allows {adminsEntityName.toLowerCase()} to manage all charges and payments.",
            [USER_TYPE.MANAGER]: "Allows {managerEntityName.toLowerCase()} to manage charges and payments for records they have access to.",
            [USER_TYPE.EXTERNAL_USER]: "Allows {extUserEntityName.toLowerCase()} to manage charges and payments for records they have access to.",
        },
    },
    [CORE_PERMISSIONS.VIEW_PAYMENT_HISTORY]: {
        group: PERMISSION_GROUPS.INVOICING,
        entityKeys: ["managerEntityName", "extUserEntityName", "adminsEntityName"],
        nameTemplate: "View Payment History",
        descriptionByUserType: {
            [USER_TYPE.ADMIN]: "Allows {adminsEntityName.toLowerCase()} to view all payment history.",
            [USER_TYPE.MANAGER]: "Allows {managerEntityName.toLowerCase()} to view payment history for records they have access to.",
            [USER_TYPE.EXTERNAL_USER]: "Allows {extUserEntityName.toLowerCase()} to view payment history for records they have access to.",
        },
        subPermission: true,
    },
    [CORE_PERMISSIONS.PERFORM_CHARGE]: {
        group: PERMISSION_GROUPS.INVOICING,
        entityKeys: ["managerEntityName", "extUserEntityName", "adminsEntityName"],
        nameTemplate: "Perform Charge",
        descriptionByUserType: {
            [USER_TYPE.ADMIN]: "Allows {adminsEntityName.toLowerCase()} to perform a charge.",
            [USER_TYPE.MANAGER]: "Allows {managerEntityName.toLowerCase()} to perform a charge for records they have access to.",
            [USER_TYPE.EXTERNAL_USER]: "Allows {extUserEntityName.toLowerCase()} to perform a charge for records they have access to.",
        },
        subPermission: true,
    },
    [CORE_PERMISSIONS.DELETE_CHARGE]: {
        group: PERMISSION_GROUPS.INVOICING,
        entityKeys: ["managerEntityName", "extUserEntityName", "adminsEntityName"],
        nameTemplate: "Delete Charge",
        descriptionByUserType: {
            [USER_TYPE.ADMIN]: "Allows {adminsEntityName.toLowerCase()} to delete charges that have not been invoiced.",
            [USER_TYPE.MANAGER]: "Allows {managerEntityName.toLowerCase()} to delete charges for records they have access to, if not yet invoiced.",
            [USER_TYPE.EXTERNAL_USER]: "Allows {extUserEntityName.toLowerCase()} to delete charges for records they have access to, if not yet invoiced.",
        },
        subPermission: true,
    },
    [CORE_PERMISSIONS.REFUND_PAYMENT]: {
        group: PERMISSION_GROUPS.INVOICING,
        entityKeys: ["managerEntityName", "extUserEntityName", "adminsEntityName"],
        nameTemplate: "Refund Payment",
        descriptionByUserType: {
            [USER_TYPE.ADMIN]: "Allows {adminsEntityName.toLowerCase()} to refund payments.",
            [USER_TYPE.MANAGER]: "Allows {managerEntityName.toLowerCase()} to refund payments for records they have access to.",
            [USER_TYPE.EXTERNAL_USER]: "Allows {extUserEntityName.toLowerCase()} to refund payments for records they have access to.",
        },
        subPermission: true,
    },
    // Other permissions
    [CORE_PERMISSIONS.MANAGE_APP_CONFIGURATIONS]: {
        group: PERMISSION_GROUPS.OTHERS,
        entityKeys: ["adminsEntityName"],
        nameTemplate: "Manage App Configurations and Integrations",
        descriptionTemplate: "Allows {adminsEntityName} to manage the App Configurations and Integrations",
    },
    [CORE_PERMISSIONS.MANAGE_API]: {
        group: PERMISSION_GROUPS.OTHERS,
        nameTemplate: "Manage API",
        descriptionTemplate: "Allows you to manage the API with which clients can read and write to your AtlasOne instance.",
    },
    [CORE_PERMISSIONS.MANAGE_BILLING]: {
        group: PERMISSION_GROUPS.OTHERS,
        entityKeys: ["adminsEntityName"],
        nameTemplate: "Manage Billing",
        descriptionTemplate: "Allows {adminsEntityName} to manage the Billing",
    },
    [CORE_PERMISSIONS.MANAGE_COMPANIES]: {
        group: PERMISSION_GROUPS.OTHERS,
        entityKeys: ["adminsEntityName"],
        nameTemplate: "Manage Companies",
        descriptionTemplate: "Allows {adminsEntityName} to create, update, and archive companies, except for those synced from third-party integrations (e.g. iinsight)",
    },
    [CORE_PERMISSIONS.MANAGE_TEMPLATES]: {
        group: PERMISSION_GROUPS.OTHERS,
        entityKeys: ["adminsEntityName"],
        nameTemplate: "Manage Templates",
        descriptionTemplate: "Allows {adminsEntityName} to create, update, and archive templates",
    },
    // AI related
    [CORE_PERMISSIONS.VIEW_QUICK_START]: {
        group: PERMISSION_GROUPS.OTHERS,
        entityKeys: ["managerEntityName", "extUserEntityName", "adminsEntityName"],
        nameTemplate: "View Quick Start",
        descriptionByUserType: {
            [USER_TYPE.ADMIN]: "Allows {adminsEntityName.toLowerCase()} to view quick start.",
            [USER_TYPE.MANAGER]: "Allows {managerEntityName.toLowerCase()} to view quick start.",
            [USER_TYPE.EXTERNAL_USER]: "Allows {extUserEntityName.toLowerCase()} to view quick start.",
        },
    },
    // Workflows and Automations
    [CORE_PERMISSIONS.MANAGE_WORKFLOWS]: {
        group: PERMISSION_GROUPS.WORKFLOWS_AND_AUTOMATIONS,
        entityKeys: ["adminsEntityName"],
        nameTemplate: "Manage Workflows",
        descriptionByUserType: {
            [USER_TYPE.ADMIN]: "Allows {adminsEntityName.toLowerCase()} to manage all workflows.",
        },
    },
    [CORE_PERMISSIONS.READ_WORKFLOWS]: {
        group: PERMISSION_GROUPS.WORKFLOWS_AND_AUTOMATIONS,
        entityKeys: ["adminsEntityName"],
        nameTemplate: "View Workflows",
        descriptionByUserType: {
            [USER_TYPE.ADMIN]: "Allows {adminsEntityName.toLowerCase()} to view all workflows.",
        },
        subPermission: true,
    },
    [CORE_PERMISSIONS.WRITE_WORKFLOWS]: {
        group: PERMISSION_GROUPS.WORKFLOWS_AND_AUTOMATIONS,
        entityKeys: ["adminsEntityName"],
        nameTemplate: "Create and Edit Workflows",
        descriptionByUserType: {
            [USER_TYPE.ADMIN]: "Allows {adminsEntityName.toLowerCase()} to create and edit workflows.",
        },
        subPermission: true,
    },
    [CORE_PERMISSIONS.DELETE_WORKFLOWS]: {
        group: PERMISSION_GROUPS.WORKFLOWS_AND_AUTOMATIONS,
        entityKeys: ["adminsEntityName"],
        nameTemplate: "Delete Workflows",
        descriptionByUserType: {
            [USER_TYPE.ADMIN]: "Allows {adminsEntityName.toLowerCase()} to delete existing workflows.",
        },
        subPermission: true,
    },
};
//# sourceMappingURL=permissionMetadata.js.map